import { Box, Button, CircularProgress, MenuItem, Switch, Typography } from "@mui/material";
import { PinnacleTextField } from "../../../components/PinnacleTextField";
import { E_FIELD_REQUIRED } from "../../../utils/constants";
import { Program } from "../../../api/interfaces/Program";
import { FormData } from "../hooks/useSubjectForm";

interface CreateSubjectDrawerContentProps {
  formData: FormData;
  touchedFields: Record<keyof FormData, boolean>;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur: (field: keyof FormData) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  programs: Program[];
  isFormValid: () => boolean;
}

const CreateSubjectDrawerContent: React.FC<CreateSubjectDrawerContentProps> = ({
  formData,
  touchedFields,
  onChange,
  onBlur,
  onSubmit,
  isSubmitting,
  programs,
  isFormValid
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <PinnacleTextField
        select
        label="Program"
        name="programId"
        value={formData.programId}
        onChange={onChange}
        fullWidth
        onBlur={() => onBlur('programId')}
        error={touchedFields.programId && !formData.programId.trim()}
        helperText={
          touchedFields.programId && !formData.programId.trim()
            ? E_FIELD_REQUIRED
            : ' '
        }
        disabled={isSubmitting}
      >
        {programs.map((program: Program) => (
          <MenuItem key={program._id} value={program._id}>
            <Typography
              noWrap
              sx={{
                maxWidth: '400px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
            >
              {program.name}
            </Typography>
          </MenuItem>
        ))}
      </PinnacleTextField>

      <PinnacleTextField
        label="Name"
        name="name"
        value={formData.name}
        onChange={onChange}
        fullWidth
        onBlur={() => onBlur('name')}
        error={touchedFields.name && !formData.name.trim()}
        helperText={
          touchedFields.name && !formData.name.trim()
            ? E_FIELD_REQUIRED
            : ' '
        }
        disabled={isSubmitting}
      />

      <PinnacleTextField
        label="Code"
        name="code"
        value={formData.code}
        onChange={onChange}
        fullWidth
        onBlur={() => onBlur('code')}
        error={touchedFields.code && !formData.code.trim()}
        helperText={
          touchedFields.code && !formData.code.trim()
            ? E_FIELD_REQUIRED
            : ' '
        }
        disabled={isSubmitting}
      />

      <PinnacleTextField
        label="Description"
        name="description"
        value={formData.description}
        onChange={onChange}
        multiline
        rows={5}
        fullWidth
        onBlur={() => onBlur('description')}
        error={touchedFields.description && !formData.description.trim()}
        helperText={
          touchedFields.description && !formData.description.trim()
            ? E_FIELD_REQUIRED
            : ' '
        }
        disabled={isSubmitting}
      />

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%" }}
      >
        <Typography variant="body2" sx={{ color: '#818181' }}>
          Set the subject as <Box component="span" sx={{ fontWeight: 'bold' }}>“TRIAL”</Box> mode?
        </Typography>

        <Switch
          checked={formData.isTrial}
          onChange={onChange}
          disabled={isSubmitting}
          name="isTrial"
        />
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={onSubmit}
        disabled={isSubmitting || !isFormValid()}
        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
      >
        CREATE
      </Button>
    </Box>
  );
};

export default CreateSubjectDrawerContent;